import type { TFunction } from 'i18next';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import {
  formatValidationErrorMessage,
  parseValidationErrorCodes,
} from '../validation/formatValidationErrorMessage';

const formatFailureReason = (
  label: string,
  count: number,
  t: TFunction,
): string =>
  t('bulkRebalance:insights.failureReasonPortfolioCount', { label, count });

const increment = (counts: Map<string, number>, label: string) => {
  counts.set(label, (counts.get(label) ?? 0) + 1);
};

export const buildRunFailureReasons = (
  warnings: string[] | null | undefined,
  audits: OptimizerAuditV2Record[],
  t: TFunction,
): string[] => {
  const warningLines = (warnings ?? []).filter((line) => line.trim());
  const sources =
    warningLines.length > 0
      ? warningLines
      : audits
          .filter((audit) => audit.passed === false)
          .flatMap((audit) => audit.validationErrors ?? []);

  const counts = new Map<string, number>();

  for (const source of sources) {
    const codes = parseValidationErrorCodes([source]);

    if (codes.length === 0) {
      increment(counts, formatValidationErrorMessage(source, t));
      continue;
    }

    for (const code of codes) {
      increment(
        counts,
        formatValidationErrorMessage(
          `Portfolio invariant violations: ${code}`,
          t,
        ),
      );
    }
  }

  return [...counts.entries()]
    .sort(
      (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
    )
    .map(([label, count]) => formatFailureReason(label, count, t));
};
